import { Upsell } from '@/@types/Upsell';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, ChevronLeft, ChevronRight as ChevronRightIcon, Link2, TrendingDown, TrendingUp, Unplug, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type ConnectionTarget = { type: 'upsell'; upsellPage: string };

interface DisconnectedUpsellsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  disconnectedUpsells: Upsell[];
  connectedUpsells: Upsell[];
  onConnect: (
    sourceUpsellId: string,
    direction: 'accept' | 'decline',
    target: ConnectionTarget,
  ) => Promise<void>;
}

export function DisconnectedUpsellsPanel({
  isOpen,
  onClose,
  disconnectedUpsells,
  connectedUpsells,
  onConnect,
}: DisconnectedUpsellsPanelProps) {
  const { t } = useTranslation('funnel');
  const [connectingItem, setConnectingItem] = useState<{
    upsellId: string;
  } | null>(null);
  const [selectedParent, setSelectedParent] = useState<string>('');
  const [selectedDirection, setSelectedDirection] = useState<'accept' | 'decline'>('accept');
  const [isLoading, setIsLoading] = useState(false);
  const [listPage, setListPage] = useState(1);
  const ITEMS_PER_PAGE = 5;

  const totalListPages = Math.ceil(disconnectedUpsells.length / ITEMS_PER_PAGE);
  const paginatedDisconnected = useMemo(
    () =>
      disconnectedUpsells.slice(
        (listPage - 1) * ITEMS_PER_PAGE,
        listPage * ITEMS_PER_PAGE,
      ),
    [disconnectedUpsells, listPage],
  );

  const handleConnect = async (disconnectedUpsell: Upsell) => {
    if (!selectedParent) return;
    setIsLoading(true);
    try {
      await onConnect(selectedParent, selectedDirection, {
        type: 'upsell',
        upsellPage: disconnectedUpsell.page,
      });
      setConnectingItem(null);
      setSelectedParent('');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="absolute top-[84px] left-4 z-50 w-80 max-h-[calc(100vh-260px)] rounded-xl bg-[#18181B]/98 backdrop-blur-md border border-[#27272a] shadow-2xl overflow-hidden flex flex-col"
        >
          <div className="flex items-center justify-between p-3 border-b border-[#27272a] flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-amber-500/15 ring-1 ring-amber-500/30">
                <Unplug className="h-3.5 w-3.5 text-amber-400" />
              </div>
              <div>
                <p className="text-xs font-semibold text-white">
                  {t('disconnected_title')}
                </p>
                <p className="text-[10px] text-zinc-500">
                  {disconnectedUpsells.length === 1 ? t('disconnected_count', { count: disconnectedUpsells.length }) : t('disconnected_count_plural', { count: disconnectedUpsells.length })}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex h-6 w-6 items-center justify-center rounded-md hover:bg-[#27272a] transition-colors"
            >
              <X className="h-3.5 w-3.5 text-zinc-500" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
            {disconnectedUpsells.length === 0 && (
              <div className="text-center py-6">
                <Unplug className="h-8 w-8 text-zinc-700 mx-auto mb-2" />
                <p className="text-[11px] text-zinc-500">
                  {t('disconnected_all_connected')}
                </p>
              </div>
            )}

            {paginatedDisconnected.map((u) => {
              const isUp = u.funnel_type === 'up_sell';
              const TypeIcon = isUp ? TrendingUp : TrendingDown;
              const isExpanded = connectingItem?.upsellId === String(u.id);

              return (
                <div
                  key={u.id}
                  className="rounded-lg border border-[#27272a] bg-[#09090b] overflow-hidden"
                >
                  <div className="flex items-center gap-2 p-2.5">
                    <div
                      className={`flex h-7 w-7 items-center justify-center rounded-md flex-shrink-0 ${
                        isUp
                          ? 'bg-emerald-500/15 ring-1 ring-emerald-500/30'
                          : 'bg-rose-500/15 ring-1 ring-rose-500/30'
                      }`}
                    >
                      <TypeIcon
                        className={`h-3.5 w-3.5 ${
                          isUp ? 'text-emerald-400' : 'text-rose-400'
                        }`}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-[10px] font-medium text-white truncate">
                          {u.name}
                        </p>
                        <span
                          className={`text-[8px] px-1 py-0.5 rounded-full flex-shrink-0 ${
                            isUp
                              ? 'bg-emerald-500/10 text-emerald-400'
                              : 'bg-rose-500/10 text-rose-400'
                          }`}
                        >
                          {isUp ? t('upsell') : t('downsell')}
                        </span>
                      </div>
                      <p className="text-[9px] text-zinc-500 truncate">
                        {u.offer?.name} • {u.offer?.price_formatted}
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        setConnectingItem(
                          isExpanded ? null : { upsellId: String(u.id) },
                        )
                      }
                      className="flex h-6 items-center gap-1 rounded-md bg-primary/10 border border-primary/20 px-2 text-[9px] font-medium text-primary hover:bg-primary/20 transition-colors flex-shrink-0"
                    >
                      <Link2 className="h-3 w-3" />
                      {isExpanded ? (
                        <ChevronDown className="h-3 w-3" />
                      ) : (
                        <ChevronRightIcon className="h-3 w-3" />
                      )}
                    </button>
                  </div>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className="overflow-hidden"
                      >
                        <div className="px-2.5 pb-2.5 space-y-2 border-t border-[#27272a] pt-2">
                          <p className="text-[10px] text-zinc-400">
                            {t('disconnected_connect_as')}
                          </p>

                          <div className="flex gap-1.5">
                            <button
                              onClick={() => setSelectedDirection('accept')}
                              className={`flex-1 py-1.5 rounded-md text-[9px] font-medium border transition-colors ${
                                selectedDirection === 'accept'
                                  ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400'
                                  : 'bg-[#18181B] border-[#27272a] text-zinc-400 hover:border-zinc-600'
                              }`}
                            >
                              {t('accept_label')}
                            </button>
                            <button
                              onClick={() => setSelectedDirection('decline')}
                              className={`flex-1 py-1.5 rounded-md text-[9px] font-medium border transition-colors ${
                                selectedDirection === 'decline'
                                  ? 'bg-red-500/15 border-red-500/30 text-red-400'
                                  : 'bg-[#18181B] border-[#27272a] text-zinc-400 hover:border-zinc-600'
                              }`}
                            >
                              {t('decline_label')}
                            </button>
                          </div>

                          <p className="text-[10px] text-zinc-400">
                            {t('disconnected_from_which')}
                          </p>

                          <Select
                            value={selectedParent}
                            onValueChange={setSelectedParent}
                          >
                            <SelectTrigger className="w-full h-8 text-[10px] bg-[#09090B] border-[#27272a]">
                              <SelectValue placeholder={t('disconnected_select')} />
                            </SelectTrigger>
                            <SelectContent>
                              {connectedUpsells.map((cu) => (
                                <SelectItem key={cu.id} value={String(cu.id)}>
                                  {cu.name} ({cu.funnel_type === 'up_sell' ? t('upsell') : t('downsell')})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          <Button
                            size="sm"
                            onClick={() => handleConnect(u)}
                            disabled={!selectedParent || isLoading}
                            className="w-full text-[10px]"
                          >
                            {isLoading ? t('disconnected_connecting') : t('disconnected_connect_to_funnel')}
                          </Button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}

            {totalListPages > 1 && (
              <div className="flex items-center justify-between pt-1.5 px-1">
                <span className="text-[9px] text-zinc-500">
                  {disconnectedUpsells.length === 1 ? t('disconnected_items_count', { count: disconnectedUpsells.length }) : t('disconnected_items_count_plural', { count: disconnectedUpsells.length })} • {t('disconnected_page', { current: listPage, total: totalListPages })}
                </span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setListPage((p) => Math.max(1, p - 1))}
                    disabled={listPage === 1}
                    className="flex h-5 w-5 items-center justify-center rounded bg-[#09090b] border border-[#27272a] hover:border-zinc-600 transition-colors disabled:opacity-30"
                  >
                    <ChevronLeft className="h-2.5 w-2.5 text-zinc-400" />
                  </button>
                  <button
                    onClick={() => setListPage((p) => Math.min(totalListPages, p + 1))}
                    disabled={listPage === totalListPages}
                    className="flex h-5 w-5 items-center justify-center rounded bg-[#09090b] border border-[#27272a] hover:border-zinc-600 transition-colors disabled:opacity-30"
                  >
                    <ChevronRightIcon className="h-2.5 w-2.5 text-zinc-400" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
